/* eslint-disable @typescript-eslint/explicit-function-return-type */
/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { call, delay, put, race } from "redux-saga/effects";

import * as mvpApi from "Services/MVP/MVPApi";

import { GENERIC_API_TIMEOUT_ERROR } from "Constants/ApplicationConstants";
import { BenefitNameEnum } from "Constants/BenefitConstants";
import { FilterValueFieldNames } from "Constants/FilterFields";
import { HttpStatusCode } from "Constants/HttpStatusCode";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IUserProfileUpdateBlockShipping } from "Dataflows/UserProfile/UserProfileAction";
import { IClaimBenefitDataResult } from "Interfaces/Benefits/IClaimBenefitDataResult";
import { IClaimBenefitResult } from "Interfaces/Benefits/IClaimBenefitResult";
import { IExtendAzureBenefitCreditResult } from "Interfaces/Benefits/IExtendAzureBenefitCreditResult";
import { IM365BenefitDetail } from "Interfaces/Benefits/IM365BenefitDetail";
import { IMicrosoft365BenefitDetail } from "Interfaces/Benefits/IMicrosoft365BenefitDetail";
import { IProfileBenefitDataResult } from "Interfaces/Benefits/IProfileBenefitDataResult";
import { ICommentAddRequest } from "Interfaces/Comments/ICommentAddRequest";
import { ICommentAddResult } from "Interfaces/Comments/ICommentAddResult";
import { ICommentsGetResult } from "Interfaces/Comments/ICommentsGetResult";
import { IApiEmptyResult, IApiErrorResult, IApiResult2 } from "Interfaces/IApiResult";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IMVPProgramProfile } from "Interfaces/MVP/IMVPProgramProfile";
import { IMVPUserProfileGetResult } from "Interfaces/MVP/IMVPUserProfileGetResult";
import { IMVPUserProfileSearchRequest } from "Interfaces/MVP/IMVPUserProfileSearchRequest";
import { IMVPUserProfileSearchResult } from "Interfaces/MVP/IMVPUserProfileSearchResult";
import { IUserProfileBlockShippingDataResult } from "Interfaces/UserProfile/IUserProfileBlockShippingDataResult";
import { IUserProfileBlockShippingRequest } from "Interfaces/UserProfile/IUserProfileBlockShippingRequest";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";

import {
  addNewCommentFail,
  addNewCommentSuccess,
  claimBenefitFail,
  claimBenefitSuccess,
  extendAzureBenefitCreditFailure,
  extendAzureBenefitCreditSuccess,
  fetchM365BenefitDetailFailure,
  fetchM365BenefitDetailSuccess,
  fetchProfileBenefitsFail,
  fetchProfileBenefitsSuccess,
  fetchProgramProfileFail,
  fetchProgramProfileSuccess,
  fetchUserProfileCommentsFail,
  fetchUserProfileCommentsSuccess,
  fetchUserProfileFail,
  fetchUserProfileSuccess,
  searchUserProfilesFail,
  searchUserProfileSuccess,
  updateM365BenefitDetailFailure,
  updateM365BenefitDetailSuccess,
  updateProgramProfileFail,
  updateProgramProfileSuccess,
  userProfileAnnualExceptionUpdateFail,
  userProfileAnnualExceptionUpdateSuccess,
  userProfileIsPrivateUpdateFail,
  userProfileIsPrivateUpdateSuccess,
  userProfilePrimaryEmailUpdateFail,
  userProfilePrimaryEmailUpdateSuccess,
  userProfileUpdateBlockShippingFail,
  userProfileUpdateBlockShippingSuccess,
  IMVPAddNewComment,
  IMVPClaimBenefit,
  IMVPExtendAzureBenefitCredit,
  IMVPFetchComments,
  IMVPFetchM365BenefitDetail,
  IMVPFetchProfileBenefits,
  IMVPFetchProgramProfile,
  IMVPFetchUserProfile,
  IMVPUpdateM365BenefitDetail,
  IMVPUpdateProgramProfile,
  IMVPUserProfilesSearch,
  IMVPUserProfileAnnualExceptionUpdate,
  IMVPUserProfileIsPrivateUpdate,
  IMVPUserProfilePrimaryEmailUpdate,
  IMVPFetchRetireProfile,
  fetchRetireProfileFail,
  fetchRetireProfileSuccess,
  IMVPRetireProfile,
  retireProfileFail,
  retireProfileSuccess,
  IMVPAddAzureBenefitDetail,
  addAzureBenefitDetailSuccess,
  addAzureBenefitDetailFail,
  IMVPUpdateMS365BenefitDetail,
  fetchMS365BenefitDetailFailure,
  fetchMS365BenefitDetailSuccess,
  updateMS365BenefitDetailFailure,
  updateMS365BenefitDetailSuccess,
} from "./MVPAction";
import { IMVPRetireProfileResponse } from "Interfaces/MVP/IMVPRetireProfileResponse";
import { IMVPAzureBenefitClaim } from "Interfaces/MVP/IMVPAzureBenefitClaim";

/**
 * Map the filter values to the request body.
 * @param {IFilterValue[]} filterValues The list of filters.
 * @return {IMVPUserProfileSearchRequest} The request to be sent.
 */
const mapFilterValuesToRequestBody = (
  filterValues: IFilterValue[]
): IMVPUserProfileSearchRequest => {
  const requestBody: IMVPUserProfileSearchRequest = {
    programStatusList: [],
    technologyAreaGroupList: [],
    technologyAreaList: [],
    clRegionList: [],
    countryRegionList: [],
    stateProvinceList: [],
    annualExceptionList: [],
    lastInvitedStartDate: undefined,
    lastInvitedEndDate: undefined,
    ndaStatusList: [],
    otherProgramMembershipList: [],
    cpmList: [],
  };

  filterValues.forEach(function (item) {
    switch (item.fieldName) {
      case FilterValueFieldNames.PROGRAM_STATUS:
        requestBody.programStatusList.push(item.value);
        break;
      case FilterValueFieldNames.TECHNOLOGY_AREA:
        requestBody.technologyAreaList.push(item.value.split("__")[0]); // Since the option value format is KEY__ID, we pick the first part as value.
        break;
      case FilterValueFieldNames.TECHNOLOGY_AREA_GROUP:
        requestBody.technologyAreaGroupList.push(item.value);
        break;
      case FilterValueFieldNames.REGION:
        requestBody.clRegionList.push(item.value);
        break;
      case FilterValueFieldNames.COUNTRY:
        requestBody.countryRegionList.push(item.value);
        break;
      case FilterValueFieldNames.STATE_PROVINCE:
        requestBody.stateProvinceList.push(item.value);
        break;
      case FilterValueFieldNames.GRANT_ANNUAL_EXCEPTION:
        requestBody.annualExceptionList.push(item.value);
        break;
      case FilterValueFieldNames.OTHER_PROGRAM_MEMBERSHIP:
        requestBody.otherProgramMembershipList.push(item.value);
        break;
      case FilterValueFieldNames.LAST_INVITED_START_DATE:
        requestBody.lastInvitedStartDate = new Date(item.value);
        break;
      case FilterValueFieldNames.LAST_INVITED_END_DATE:
        requestBody.lastInvitedEndDate = new Date(item.value);
        break;
      case FilterValueFieldNames.NDA_STATUS:
        requestBody.ndaStatusList.push(item.value);
        break;
      case FilterValueFieldNames.AVAILABLE_CPM_LIST:
        requestBody.cpmList.push(item.value);
      case FilterValueFieldNames.OVDF_BLOCK:
        requestBody.isOvdfBlock = item.value === "true";
        break;
      default:
        return;
    }
  });
  return requestBody;
};

/**
 * Generator function to async dispatch action to fetch a profiles benefits
 * @generator
 * @param {IMVPFetchProfileBenefits} action To fetch a profiles benefits.
 * @returns {void}
 */
function* handleMVPFetchProfileBenefits(action: IMVPFetchProfileBenefits): Generator {
  const defaultFailedErrorMessage = "Failed to fetch profile benefits";

  try {
    const { result, timeout }: IApiResult2<IProfileBenefitDataResult> | any = yield race({
      result: call(mvpApi.getProfileBenefits, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchProfileBenefitsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(fetchProfileBenefitsSuccess(result.parsedBody));
        break;
      case HttpStatusCode.BadRequest:
        yield put(fetchProfileBenefitsFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(fetchProfileBenefitsFail(defaultFailedErrorMessage));
    }
  } catch (error) {
    yield put(fetchProfileBenefitsFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action to claim a benefit for a user profile
 * @generator
 * @param {IMVPClaimBenefit} action To claim benefit for MVP user profile.
 * @description This function is used to claim a yaml based benefits for a user profile.
 * @returns
 */
function* handleMVPClaimBenefitManager(action: IMVPClaimBenefit): Generator {
  const failedMessage =
    "API call failed, please try again and let us know if issue is not resolved.";

  try {
    const { result, timeout }: IApiResult2<IClaimBenefitDataResult> | any = yield race({
      result: call(mvpApi.claimUserProfileBenefitManager, action.benefitType, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(claimBenefitFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.CreatedAt:
        const requestResult = result.parsedBody as IClaimBenefitResult;
        if (!requestResult.isSuccess) {
          yield put(claimBenefitFail(failedMessage));
        }
        yield put(claimBenefitSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(claimBenefitFail((result.parsedBody as IApiErrorResult).error.message));
      default:
        yield put(claimBenefitFail((result.parsedBody as IApiErrorResult).error.message));
    }
  } catch (error) {
    yield put(claimBenefitFail(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IUpdateProgramProfile} action To claim program profile benefit.
 * @returns {void}
 */
function* handleMVPClaimBenefit(action: IMVPClaimBenefit): Generator {
  const failedMessage =
    "API call failed, please try again and let us know if issue is not resolved.";

  const benefitApiMap = {
    [BenefitNameEnum.GitHubPro]: mvpApi.claimUserProfileBenefitsGithubPro,
    [BenefitNameEnum.MCP]: mvpApi.claimUserProfileBenefitsMcp,
    [BenefitNameEnum.LinkedInLearning]: mvpApi.claimUserProfileBenefitsLinkedIn,
    [BenefitNameEnum.M365]: mvpApi.claimUserProfileBenefitsM365,
    [BenefitNameEnum.MS365]: mvpApi.claimUserProfileBenefitsMS365,
    [BenefitNameEnum.Teams]: mvpApi.claimUserProfileBenefitsTeams,
    [BenefitNameEnum.AwardKits]: mvpApi.claimUserProfileBenefitsAwardKits,
  };

  try {
    const apiCall = benefitApiMap[action.benefitType];
    const { result, timeout }: IApiResult2<IClaimBenefitDataResult> | any = yield race({
      result: call(apiCall, action.userProfileId, action.Sku ? action.Sku : ""),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(claimBenefitFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.CreatedAt:
        const requestResult = result.parsedBody as IClaimBenefitResult;
        if (!requestResult.isSuccess) {
          yield put(claimBenefitFail(failedMessage));
        }
        yield put(claimBenefitSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(claimBenefitFail((result.parsedBody as IApiErrorResult).error.message));
      default:
        yield put(claimBenefitFail((result.parsedBody as IApiErrorResult).error.message));
    }
  } catch (error) {
    yield put(claimBenefitFail(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPFetchComments} action To get user's comments.
 * @returns {void}
 */
function* handleMVPCommentsFetch(action: IMVPFetchComments) {
  const defaultFailedErrorMessage = "Failed to fetch user profile comments";
  const { mvpUserProfileId } = action;
  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.fetchUserComments, mvpUserProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchUserProfileCommentsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const { userProfileComments } = result.parsedBody as ICommentsGetResult;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(fetchUserProfileCommentsSuccess(userProfileComments));
        break;
      case HttpStatusCode.BadRequest:
        yield put(fetchUserProfileCommentsFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(fetchUserProfileCommentsFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(fetchUserProfileCommentsFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPAddNewComment} action To add comment.
 * @returns {void}
 */
function* handleMVPAddComment(action: IMVPAddNewComment) {
  const defaultFailedErrorMessage = "Failed to comment";
  const { newComment, userProfileId, createdBy } = action;
  const requestBody: ICommentAddRequest = {
    userProfileComment: {
      userProfileId,
      text: newComment,
      createdBy,
      tenantName: TenantTypesEnum.MVP,
    },
  };

  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.addNewComment, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(addNewCommentFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const { userProfileId } = result.parsedBody as ICommentAddResult;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(addNewCommentSuccess(userProfileId));
        break;
      case HttpStatusCode.BadRequest:
        yield put(addNewCommentFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(addNewCommentFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(addNewCommentFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPFetchProgramProfile} action To get Program profile.
 * @returns {void}
 */
function* handleMVPProgramProfileFetch(action: IMVPFetchProgramProfile) {
  const defaultFailedErrorMessage = "Failed to fetch Program profile";
  const { programProfileId } = action;
  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.fetchProgramProfile, programProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchProgramProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const programProfile = result.parsedBody as IMVPProgramProfile;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(fetchProgramProfileSuccess(programProfile));
        break;
      case HttpStatusCode.BadRequest:
        yield put(fetchProgramProfileFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(fetchProgramProfileFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(fetchProgramProfileFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPFetchProgramProfile} action To get Program profile.
 * @returns {void}
 */
function* handleMVPRetireProfileFetch(action: IMVPFetchRetireProfile) {
  const defaultFailedErrorMessage = "Failed to fetch retire profile";
  const { userProfileId } = action;
  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.fetchRetireProfile, userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchRetireProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const retireProfile = result.parsedBody as IMVPRetireProfileResponse;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(fetchRetireProfileSuccess(retireProfile));
        break;
      case HttpStatusCode.BadRequest:
        yield put(fetchRetireProfileFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(fetchRetireProfileFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(fetchRetireProfileFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPUpdateProgramProfile} action To update program profile.
 * @returns {void}
 */
function* handleMVPUpdateProgramProfile(action: IMVPUpdateProgramProfile) {
  const defaultFailedErrorMessage = "Failed to update program profile";

  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.updateProgramProfile, action.programProfile),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateProgramProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(updateProgramProfileSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(updateProgramProfileFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(updateProgramProfileFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(updateProgramProfileFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPAddAzureBenefitDetail} action To update azure benefit .
 * @returns {void}
 */
function* handleAddAzureBenefit(action: IMVPAddAzureBenefitDetail) {
  const failedMessage = "Failed to add Azure Benefit ";
  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.saveAzureBenefitClaim, action.azureBenefit, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(addAzureBenefitDetailFail(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        const requestResult = result.parsedBody;
        if (!requestResult.isSuccess) {
          yield put(addAzureBenefitDetailFail(failedMessage));
        }
        yield put(addAzureBenefitDetailSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(addAzureBenefitDetailFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(addAzureBenefitDetailFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(addAzureBenefitDetailFail(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPRetireProfile} action To update program profile.
 * @returns {void}
 */
function* handleMVPRetireProfile(action: IMVPRetireProfile) {
  const defaultFailedErrorMessage = "Failed to retire profile";

  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.retireProfile, action.retireProfile),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(retireProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(retireProfileSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(retireProfileFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(retireProfileFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(retireProfileFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPFetchUserProfile} action To get user profile.
 * @returns {void}
 */
function* handleMVPUserProfileFetch(action: IMVPFetchUserProfile) {
  const defaultFailedErrorMessage = "Failed to fetch user profile";
  const { mvpUserProfileId } = action;
  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.fetchUserProfile, mvpUserProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchUserProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const { userProfile } = result.parsedBody as IMVPUserProfileGetResult;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(fetchUserProfileSuccess(userProfile));
        break;
      case HttpStatusCode.BadRequest:
        yield put(fetchUserProfileFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(fetchUserProfileFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(fetchUserProfileFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPUserProfilesSearch} action To search user profile for given text.
 * @returns {void}
 */
function* handleMVPSearchUserProfiles(action: IMVPUserProfilesSearch) {
  const failedMessage = "Failed to search the user profiles.";
  const requestBody = mapFilterValuesToRequestBody(action.selectedFilterValues);
  requestBody.searchKey = action.searchText;
  requestBody.pageIndex = action.searchPage;
  requestBody.pageSize = action.pageSize;
  requestBody.sortColumn = action.sortColumn;
  requestBody.sortDescending = action.sortDescending;

  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.searchUserProfiles, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(searchUserProfilesFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const { userProfiles, filteredCount } = result.parsedBody as IMVPUserProfileSearchResult;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          searchUserProfileSuccess(filteredCount, action.searchPage, action.pageSize, userProfiles)
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(searchUserProfilesFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(searchUserProfilesFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(searchUserProfilesFail(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPFetchM365BenefitDetail} action To get the users M365 Benefit Details.
 * @returns {void}
 */
function* handleMVPGetM365BenefitDetail(action: IMVPFetchM365BenefitDetail) {
  const failedMessage = "Failed to get O365 Benefit Details";

  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.getM365BenefitDetails, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchM365BenefitDetailFailure(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(fetchM365BenefitDetailSuccess(result.parsedBody as IM365BenefitDetail));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          fetchM365BenefitDetailFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(fetchM365BenefitDetailFailure(failedMessage));
        break;
    }
  } catch (error) {
    yield put(fetchM365BenefitDetailFailure(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPUpdateM365BenefitDetail} action To update/create the users M365 Benefit Details.
 * @returns {void}
 */
function* handleMVPUpdateM365BenefitDetail(action: IMVPUpdateM365BenefitDetail) {
  const failedMessage = "Failed to update M365 Benefit Details";

  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.updateM365BenefitDetails, action.m365BenefitDetail),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateM365BenefitDetailFailure(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.CreatedAt:
        yield put(updateM365BenefitDetailSuccess(result.parsedBody as IM365BenefitDetail));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          updateM365BenefitDetailFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(updateM365BenefitDetailFailure(failedMessage));
        break;
    }
  } catch (error) {
    yield put(updateM365BenefitDetailFailure(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPFetchM365BenefitDetail} action To get the users Microsoft 365 Benefit Details.
 * @returns {void}
 */
function* handleMVPGetMS365BenefitDetail(action: IMVPFetchM365BenefitDetail) {
  const failedMessage = "Failed to get O365 Benefit Details";

  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.getMS365BenefitDetails, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchMS365BenefitDetailFailure(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(fetchMS365BenefitDetailSuccess(result.parsedBody as IMicrosoft365BenefitDetail));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          fetchMS365BenefitDetailFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(fetchMS365BenefitDetailFailure(failedMessage));
        break;
    }
  } catch (error) {
    yield put(fetchMS365BenefitDetailFailure(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPUpdateM365BenefitDetail} action To update/create the users Microsoft 365 Benefit Details.
 * @returns {void}
 */
function* handleMVPUpdateMS365BenefitDetail(action: IMVPUpdateMS365BenefitDetail) {
  const failedMessage = "Failed to update M365 Benefit Details";

  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.updateMS365BenefitDetails, action.ms365BenefitDetail),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateMS365BenefitDetailFailure(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.CreatedAt:
        yield put(updateMS365BenefitDetailSuccess(result.parsedBody as IMicrosoft365BenefitDetail));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          updateMS365BenefitDetailFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(updateMS365BenefitDetailFailure(failedMessage));
        break;
    }
  } catch (error) {
    yield put(updateMS365BenefitDetailFailure(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPExtendAzureBenefitCredit} action To update/create the users M365 Benefit Details.
 * @returns {void}
 */
function* handleMVPExtendAzureBenefitCredit(action: IMVPExtendAzureBenefitCredit) {
  const failedMessage = "Failed to extend Azure Benefit Credit";

  try {
    const { result, timeout } = yield race({
      result: call(mvpApi.extendAzureBenefitCredit, action.credit, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateM365BenefitDetailFailure(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        const requestResult = result.parsedBody as IExtendAzureBenefitCreditResult;
        if (!requestResult.success) {
          yield put(extendAzureBenefitCreditFailure(failedMessage));
        }
        yield put(extendAzureBenefitCreditSuccess(requestResult.capCredit));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          extendAzureBenefitCreditFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(extendAzureBenefitCreditFailure(failedMessage));
        break;
    }
  } catch (error) {
    yield put(extendAzureBenefitCreditFailure(failedMessage));
  }
}

/**
 * Handle calling the api to update a user profile asynchronously.
 * @param {IUpdateUserProfile} action To update user profile.
 * @returns {Generator} the profile actions.
 */
function* handleMVPUpdateUserProfileBlockShipping(
  action: IUserProfileUpdateBlockShipping
): Generator {
  const failedMessage = "Failed to update the shipping status for this user profile.";

  const userProfileToUpdate: IUserProfileBlockShippingRequest = {
    userProfileId: action.userProfileId,
    blockShipping: action.isShippingBlocked,
  };

  try {
    const { result, timeout }: IApiResult2<IUserProfileBlockShippingDataResult> | any = yield race({
      result: call(mvpApi.updateUserProfileBlockShipping, userProfileToUpdate),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(userProfileUpdateBlockShippingFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(userProfileUpdateBlockShippingSuccess(result.parsedBody as number));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          userProfileUpdateBlockShippingFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(userProfileUpdateBlockShippingFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(userProfileUpdateBlockShippingFail(error.toString()));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IUserProfileDlLoad} action To remove dl.
 * @returns {void}
 */
function* handleMVPUserProfileIsPrivateUpdate(action: IMVPUserProfileIsPrivateUpdate) {
  const failedMessage = "Failed to set the is private flag for profile";

  try {
    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(mvpApi.updateUserProfileIsPrivate, action.userProfileId, action.isPrivate),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(userProfileIsPrivateUpdateFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(userProfileIsPrivateUpdateSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          userProfileIsPrivateUpdateFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(userProfileIsPrivateUpdateFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(userProfileIsPrivateUpdateFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * @param action To update primary email for profile.
 * @returns {Generator}
 */
function* handleMVPUserProfilePrimaryEmailUpdate(
  action: IMVPUserProfilePrimaryEmailUpdate
): Generator {
  const failedMessage = "Failed to set the primary email for profile";

  try {
    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(mvpApi.updateUserProfilePrimaryEmail, action.userProfileId, action.newEmail),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(userProfilePrimaryEmailUpdateFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(userProfilePrimaryEmailUpdateSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          userProfilePrimaryEmailUpdateFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(userProfilePrimaryEmailUpdateFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(
      userProfilePrimaryEmailUpdateFail(
        `Handle Update Primary Email API failed: ${error as string}`
      )
    );
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPUserProfileAnnualExceptionUpdate} action To update userprofile annual exception.
 * @returns {void}
 */
function* handleMVPUserProfileAnnualExceptionUpdate(action: IMVPUserProfileAnnualExceptionUpdate) {
  const failedMessage =
    "Failed to set the annual exception, please check if this user is eligible for renewal and renew application exists";

  try {
    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(
        mvpApi.updateUserProfileAnnualException,
        action.userProfileId,
        action.justification
      ),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(userProfileAnnualExceptionUpdateFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(userProfileAnnualExceptionUpdateSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          userProfileAnnualExceptionUpdateFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(userProfileAnnualExceptionUpdateFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(userProfileAnnualExceptionUpdateFail(error));
  }
}

export {
  handleMVPSearchUserProfiles,
  handleMVPFetchProfileBenefits,
  handleMVPClaimBenefitManager,
  handleMVPClaimBenefit,
  handleMVPCommentsFetch,
  handleMVPAddComment,
  handleMVPUserProfileFetch,
  handleMVPProgramProfileFetch,
  handleMVPRetireProfileFetch,
  handleMVPUpdateProgramProfile,
  handleAddAzureBenefit,
  handleMVPRetireProfile,
  handleMVPGetM365BenefitDetail,
  handleMVPUpdateM365BenefitDetail,
  handleMVPGetMS365BenefitDetail,
  handleMVPUpdateMS365BenefitDetail,
  handleMVPUpdateUserProfileBlockShipping,
  handleMVPExtendAzureBenefitCredit,
  handleMVPUserProfileIsPrivateUpdate,
  handleMVPUserProfilePrimaryEmailUpdate,
  handleMVPUserProfileAnnualExceptionUpdate,
};
